import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faArrowLeft, faFire, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactJason } from 'react-jason';
import { ClinicalEvidenceCard } from './clinical-evidence-card';
import { EvidenceSummary } from './evidence-summary';
import { confidenceLevelToRating } from './risk-gap-confidence-level';
import { riskGapStatusBadgeDefinition } from './risk-gap-status-badge-definition';
import { riskGapTypeBadgeDefinition } from './risk-gap-type-badge-definition';

export interface RiskGapDetailViewProps {
  riskGap: RiskGapModel;
  onClose?: () => void;
}

const ClinicalEvidence = ({ riskGap }: { riskGap: RiskGapModel }) => {
  return (
    <div>
      {riskGap.evidenceReferences.length > 0 ? (
        <div className={tw`flex flex-wrap gap-4`}>
          {riskGap.evidenceReferences.map((reference) => (
            <ClinicalEvidenceCard
              key={`${reference.resourceType}-${reference.resourceId}`}
              reference={reference}
            />
          ))}
        </div>
      ) : (
        <div className={tw`text-content-subtle text-sm`}>No clinical evidence available.</div>
      )}
    </div>
  );
};

export const RiskGapDetailView = ({ riskGap, onClose }: RiskGapDetailViewProps) => {
  const { breakpoints } = useBreakpointQuery();
  const typeBadge = riskGapTypeBadgeDefinition(riskGap.type);
  const statusBadge = riskGapStatusBadgeDefinition(riskGap);
  const confidenceRating =
    riskGap.type === 'suspect' ? confidenceLevelToRating(riskGap.confidenceLevel) : undefined;
  let confidenceBadge:
    | {
        content: string;
      }
    | undefined;
  if (confidenceRating === 'high') {
    confidenceBadge = { content: 'High confidence' };
  } else if (confidenceRating === 'medium') {
    confidenceBadge = { content: 'Medium confidence' };
  } else if (confidenceRating === 'low') {
    confidenceBadge = { content: 'Low confidence' };
  }
  const statusSectionClassName = twx('p-4', {
    'bg-info-background': riskGap.type === 'net-new',
    'bg-caution-background': riskGap.type === 'suspect',
    'bg-success-background': riskGap.type === 'recapture',
  });
  const telemetry = useTelemetry();
  const { openModal } = useModal({
    boundaryName: 'ViewFHIRModal',
    layout: { fullbleed: false, mode: 'full', overflowBehavior: 'scroll', showCloseButton: true },
  });

  const handleViewFHIR = () => {
    openModal({
      telemetry,
      action: {
        content: {
          body: (
            <>
              <Heading level="h2" className={tw`text-lg`}>
                View FHIR
              </Heading>
              <div className={tw`h-full text-sm [&_pre]:overflow-auto`}>
                <ReactJason value={riskGap.resource} />
              </div>
            </>
          ),
        },
      },
    });
  };

  return (
    <div
      className={twx('flex h-full max-h-full w-0 flex-col overflow-hidden bg-white', {
        'w-full max-w-full': riskGap,
      })}
    >
      <div className={tw`flex w-full items-start justify-between gap-2 overflow-visible px-5 py-4`}>
        <div className={tw`flex flex-1 items-center gap-2`}>
          {onClose && (
            <Button
              ariaLabel={breakpoints.isAtLeast.lg ? 'Close' : 'Back'}
              type="button"
              size="sm"
              variant="circular"
              className={tw`flex min-w-8 items-center justify-center rounded-full bg-content-main p-2 hover:bg-content-subtle/25 active:bg-content-subtle/25`}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={breakpoints.isAtLeast.lg ? faXmark : faArrowLeft} />
            </Button>
          )}
          <h2 className={tw`font-semibold text-content-main text-lg`}>
            {riskGap.title} ({riskGap.hccCode})
          </h2>
          <Badge {...typeBadge} size="sm" />
        </div>
        <Button
          ariaLabel="View FHIR"
          type="button"
          size="sm"
          variant="link"
          className={tw`flex rounded-b border p-2 text-content-subtle hover:text-content-main`}
          onClick={handleViewFHIR}
          telemetryInteractionName="resource_action_click"
        >
          <FontAwesomeIcon icon={faFire} className={tw`h-4 w-4`} />
        </Button>
      </div>
      <HorizontalDivider className={tw`my-0`} />

      {/* Content */}
      <div className={tw`flex flex-1 flex-col overflow-auto`}>
        <div className={statusSectionClassName}>
          <div className={tw`mb-3`}>
            <div
              className={tw`mb-1 font-medium text-content-subtle text-xs uppercase tracking-wider`}
            >
              Status
            </div>
            <div className={tw`flex flex-wrap items-center gap-2`}>
              <Badge
                className={tw`bg-white`}
                content={statusBadge.content}
                icon={undefined}
                size="sm"
                variant="meta"
              />
              {confidenceBadge && (
                <Badge
                  className={tw`bg-white`}
                  content={confidenceBadge.content}
                  icon={undefined}
                  size="sm"
                  variant="meta"
                />
              )}
              <span className={tw`text-content-subtle text-xs`}>
                {formatFHIRDate(riskGap.lastStatusUpdateDate)} by {riskGap.lastUpdatedByDisplay}
              </span>
            </div>
          </div>

          {/* ICD-10 Codes */}
          {riskGap.icd10Codes.length > 0 && (
            <div>
              <div
                className={tw`mb-2 font-medium text-content-subtle text-xs uppercase tracking-wider`}
              >
                ICD-10 Code
              </div>
              <div className={tw`grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2`}>
                {riskGap.icd10Codes.map((code) => (
                  <div
                    key={code.code}
                    className={tw`flex h-full flex-col gap-1.5 rounded-lg border border-divider-main bg-white p-2.5`}
                  >
                    <div className={tw`text-sm leading-snug`}>
                      <span className={tw`font-bold`}>{code.code}</span>{' '}
                      {code.display && <span>{code.display}</span>}
                    </div>
                    {code.lastEncounterDiagnosisRecordedDate && (
                      <div className={tw`mt-auto text-content-subtle text-xs`}>
                        Last recorded {formatFHIRDate(code.lastEncounterDiagnosisRecordedDate)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <HorizontalDivider className={tw`my-0`} />
        <div className={tw`space-y-4 p-4`}>
          <EvidenceSummary riskGap={riskGap} />
          <ClinicalEvidence riskGap={riskGap} />
        </div>
      </div>
    </div>
  );
};
